import { Icons } from '@/assets/icons/icons';
import { type ModalFC } from '@/components/modals/modalsManager';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Separator } from '@/components/ui/Separator';
import { isMac } from '@/utils/capabilities';

export const GoogleAccessibilityModal: ModalFC<{}> = ({
  open,
  onOpenChange,
}) => {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-w-xl w-full bg-background overflow-y-auto scrollbar-hide items-start content-start p-8">
        <Dialog.Header className="space-y-2">
          <Dialog.Title className="text-xl">
            Enable Google Accessibility
          </Dialog.Title>
          <Dialog.Description className="text-sm leading-relaxed">
            Littlebird needs screen reader support enabled to read your Google
            Documents
          </Dialog.Description>
        </Dialog.Header>

        <YStack className="gap-6 w-full mt-2">
          <KeyboardShortcutMethod />
          <OrDivider />
          <MenuMethod />
          <Separator />
          <SuccessIndicator />
          <p className="text-xs text-foreground-muted leading-relaxed">
            You only need to do this once — the setting is saved to your Google
            account.
          </p>
        </YStack>
      </Dialog.Content>
    </Dialog.Root>
  );
};

function KeyboardShortcutMethod() {
  const onMac = isMac();
  return (
    <YStack className="gap-4">
      <span className="text-sm font-medium">Keyboard shortcut (fastest)</span>
      <p className="text-sm text-foreground-muted">
        With a Google Document open, press:
      </p>
      <XStack className="gap-2 items-center">
        <Kbd>{onMac ? 'Cmd' : 'Ctrl'}</Kbd>
        <span className="text-foreground-muted text-xs">+</span>
        <Kbd>{onMac ? 'Option' : 'Alt'}</Kbd>
        <span className="text-foreground-muted text-xs">+</span>
        <Kbd>Z</Kbd>
      </XStack>
    </YStack>
  );
}

function OrDivider() {
  return (
    <XStack className="items-center gap-3 w-full">
      <Separator className="flex-1" />
      <span className="text-xs font-medium text-foreground-muted uppercase tracking-wider">
        or
      </span>
      <Separator className="flex-1" />
    </XStack>
  );
}

function MenuMethod() {
  return (
    <YStack className="gap-4">
      <span className="text-sm font-medium">Use the menu</span>
      <YStack className="gap-3">
        <MenuStep number={1} text="Open the" bold="Tools" suffix="menu" />
        <MenuStep number={2} text="Click" bold="Accessibility" />
        <MenuStep
          number={3}
          text="Check"
          bold="Turn on screen reader support"
        />
        <MenuStep number={4} text="Click" bold="OK" />
      </YStack>
    </YStack>
  );
}

function SuccessIndicator() {
  return (
    <YStack className="gap-3">
      <span className="text-sm font-medium">How to know it worked</span>
      <XStack className="items-center gap-3 rounded-lg border border-border-strong bg-surface/50 p-4">
        <Icons.circleCheck className="size-5 text-foreground-muted shrink-0" />
        <p className="text-sm text-foreground-muted leading-relaxed">
          A new{' '}
          <strong className="text-foreground font-medium">Accessibility</strong>{' '}
          menu will appear in the menu bar of your document.
        </p>
      </XStack>
    </YStack>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center px-3 py-2 rounded-lg bg-surface border border-border-strong text-sm font-mono font-medium text-foreground shadow-[0_2px_0_0_rgba(0,0,0,0.15)]">
      {children}
    </kbd>
  );
}

function MenuStep({
  number,
  text,
  bold,
  suffix,
}: {
  number: number;
  text: string;
  bold: string;
  suffix?: string;
}) {
  return (
    <XStack className="items-baseline gap-2.5 text-sm text-foreground-muted">
      <span className="text-xs font-medium text-foreground-muted/60 w-3 shrink-0">
        {number}.
      </span>
      <span>
        {text} <strong className="text-foreground font-medium">{bold}</strong>
        {suffix && ` ${suffix}`}
      </span>
    </XStack>
  );
}
